import React, { useState, useEffect } from 'react';

// =================================================================================================
// API CONNECTION
// =================================================================================================
const API_BASE = 'https://nirvikalp.integrashion.com/api';

const saveCostingToDB = async (data) => {
    try {
        const payload = { category: 'costing', data: data };
        const response = await fetch(`${API_BASE}/save_universal.php`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload)
        });
        
        // Handle "unexpected end of data" gracefully by ensuring we get text first
        const text = await response.text();
        if (!text) throw new Error("Empty response from server");
        
        const result = JSON.parse(text);
        if (!result.success) throw new Error(result.error || 'Save failed');
        
        alert("Costing Saved Successfully! Version: " + result.version);
        return result;
    } catch (err) {
        console.error('Costing save error:', err);
        alert('Failed to save costing: ' + err.message);
        return null;
    }
};

export default function CostingSummary({ db, ws, setWs, currency, setCurrency, activeBlock, setActiveBlock, fobTotal, saveCosting, loadCosting, deleteCosting, openStylePassport }) {
  const [filter, setFilter] = useState({ brand: "", style: "" });
  const [imgSearchLoading, setImgSearchLoading] = useState(false);
  const [imgSearchHint,    setImgSearchHint]    = useState("");

  // --- FIX: AUTO-DETECT DEFAULT BASE CURRENCY ---
  useEffect(() => {
     if (db.currencies && db.currencies.length > 0) {
        const definedBase = db.currencies.find(c => c.isBase);
        if (definedBase && currency.id !== definedBase.id) {
           if (currency.name !== definedBase.name) {
              setCurrency(definedBase);
           }
        } else if (!definedBase && (!currency.name || currency.name === '')) {
           setCurrency(db.currencies[0]);
        }
     }
  }, [db.currencies]);

  const baseCurrency = db.currencies?.find(c => c.isBase) || { rate: 1, name: 'USD' };
  const baseRate = baseCurrency.rate || 1;
  const currentRate = currency.rate || 1;
  const conversionFactor = currentRate / baseRate;
  const formatMoney = (value) => {
     const fallbackBaseName = db.currencies?.find(c => c.isBase)?.name || 'USD';
     try {
        return new Intl.NumberFormat('en-US', {
           style: 'currency',
           currency: currency.name || fallbackBaseName,
           minimumFractionDigits: 2,
           maximumFractionDigits: 3
        }).format(value);
     } catch {
        return `${currency.name || fallbackBaseName} ${(parseFloat(value)||0).toFixed(3)}`;
     }
  };

  // FOB always shown in base currency — never converted
  const formatBaseMoney = (value) => {
     try {
        return new Intl.NumberFormat('en-US', {
           style: 'currency',
           currency: baseCurrency.name || 'USD',
           minimumFractionDigits: 2,
           maximumFractionDigits: 3
        }).format(value);
     } catch {
        return `${baseCurrency.name} ${(parseFloat(value)||0).toFixed(3)}`;
     }
  };

  const sumTotal = (cat) => {
     const baseSum = (ws[cat] || []).reduce((a, b) => a + (parseFloat(b.total) || 0), 0);
     return baseSum * conversionFactor;
  };

  const downloadExcel = () => {
     let csv = "COSTING SHEET,GENERATED BY FAIRCM 4.0\n\n";
     const styleRef = db.mgmtStyles.find(s=>s.id===ws.styleId)?.styleRef || 'Unknown';
     
     csv += `STYLE:,${styleRef}\n`;
     csv += `CURRENCY:,${currency.name} (Rate: ${currentRate})\n\n`;
     csv += "CATEGORY,ITEM,SUPPLIER,WIDTH,CONS,RATE,TOTAL\n";

     const addRows = (cat, label) => {
        if(ws[cat] && ws[cat].length > 0) {
            csv += `${label}\n`;
            ws[cat].forEach(i => {
               const dispPrice = (parseFloat(i.price)||0) * conversionFactor;
               const dispTotal = (parseFloat(i.total)||0) * conversionFactor;
               csv += `,${i.item||'-'},${i.supplier||'-'},${i.width||'-'},${i.con||0},${dispPrice.toFixed(3)},${dispTotal.toFixed(3)}\n`;
            });
        }
     };
     
     addRows("material", "MATERIAL");
     addRows("trims", "TRIMS");
     addRows("va", "VA");
     addRows("finishing", "FINISHING");

     const styleData = db.mgmtStyles.find(s=>s.id===ws.styleId);
     let liveSMV = 0;
     if (styleData?.workstationData?.aggregatedOps) {
         liveSMV = styleData.workstationData.aggregatedOps.reduce((sum, op) => sum + (parseFloat(op.smv)||0), 0);
     } else {
         liveSMV = parseFloat(styleData?.workstationData?.smv || styleData?.smv || ws.cm.sam || 0);
     }
     
     const cmBase = ws.cm.cost > 0 ? parseFloat(ws.cm.cost) : (liveSMV * ws.cm.cpm)/(ws.cm.eff/100);
     const cmDisp = cmBase * conversionFactor;
     const cpmDisp = ws.cm.cpm * conversionFactor;

     csv += `\nCM COST,SMV:${liveSMV.toFixed(3)},EFF:${ws.cm.eff}%,,CPM:${cpmDisp.toFixed(3)},,${cmDisp.toFixed(3)}\n`;
     csv += `OH & PROFIT,OH: ${ws.comm.oh}%,PROFIT: ${ws.comm.profit}%\n`;
     csv += `GRAND TOTAL (FOB),,,,,,${(fobTotal * conversionFactor).toFixed(3)}\n`;

     const blob = new Blob([csv], { type: 'text/csv' });
     const url = URL.createObjectURL(blob);
     const a = document.createElement('a');
     a.href = url;
     a.download = `Costing_${styleRef}_${currency.name}.csv`;
     a.click();
  };

  if (activeBlock === "DASHBOARD") {
    const dashboardData = (db.mgmtStyles || []).map(style => {
       const cost = db.costings.find(c => c.styleId === style.id);
       
       if (!cost) {
          return { 
             ...style, 
             hasCost: false, m:0, t:0, v:0, f:0, cm:0, fob: 0, sam: 0,
             costStatus: "Pending IA" 
          };
       }
       
       const sumStored = (cat) => (cost.data[cat] || []).reduce((a,b)=>a+(parseFloat(b.total)||0),0);
       const mBase = sumStored("material"); 
       const tBase = sumStored("trims"); 
       const vBase = sumStored("va"); 
       const fBase = sumStored("finishing");
       
       let liveSMV = 0;
       if (style.workstationData?.aggregatedOps && Array.isArray(style.workstationData.aggregatedOps)) {
           liveSMV = style.workstationData.aggregatedOps.reduce((sum, op) => sum + (parseFloat(op.smv) || 0), 0);
       } else {
           liveSMV = parseFloat(style.workstationData?.smv || style.smv || cost.data.cm.sam || 0);
       }

       const cmBase = cost.data.cm.cost > 0 
          ? parseFloat(cost.data.cm.cost) 
          : (liveSMV * cost.data.cm.cpm) / (cost.data.cm.eff / 100);

       const subTotal = mBase + tBase + vBase + fBase + cmBase;
       const fobBase = subTotal * (1 + (parseFloat(cost.data.comm.oh) + parseFloat(cost.data.comm.profit))/100);

       return { 
          ...style, 
          hasCost: true, 
          m: mBase * conversionFactor, 
          t: tBase * conversionFactor, 
          v: vBase * conversionFactor, 
          f: fBase * conversionFactor, 
          cm: cmBase * conversionFactor,
          sam: liveSMV, 
          fob: fobBase,          // always base currency — displayed with formatBaseMoney
          costStatus: cost.data.status || "Pending IA" 
       };
    }).filter(s => 
       (filter.brand === "" || s.brand === filter.brand) && 
       (filter.style === "" || s.styleRef.toLowerCase().includes(filter.style.toLowerCase()))
    );

    return (
       <div className="layout-master" style={{ display: 'block' }}>
          <h3 style={{color:'#1f1f1f', marginBottom:'20px'}}>COSTING DASHBOARD</h3>
          
          <div className="card" style={{ marginBottom:'16px' }}>
             {/* ── 4-column equal filter row ── */}
             <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'12px', alignItems:'end' }}>

               {/* 1. Brand filter */}
               <div className="field" style={{ margin:0 }}>
                 <label>Filter Brand</label>
                 <select value={filter.brand} onChange={e=>setFilter({...filter, brand:e.target.value})}>
                   <option value="">All Brands</option>
                   {(db.brands || []).map(b=><option key={b.id}>{b.name}</option>)}
                 </select>
               </div>

               {/* 2. Style text search */}
               <div className="field" style={{ margin:0 }}>
                 <label>Search Style</label>
                 <input
                   value={filter.style}
                   onChange={e=>setFilter({...filter, style:e.target.value})}
                   placeholder="Type style ref..."
                 />
               </div>

               {/* 3. Currency */}
               <div className="field" style={{ margin:0 }}>
                 <label>View Currency</label>
                 <select
                   onChange={e => setCurrency(db.currencies.find(c => c.name === e.target.value) || currency)}
                   value={currency.name}
                   style={{fontWeight:'bold', color:'#1565C0'}}
                 >
                   {(db.currencies || []).map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                 </select>
               </div>

               {/* 4. Image-based AI search */}
               <div className="field" style={{ margin:0 }}>
                 <label style={{ display:'flex', alignItems:'center', gap:'5px' }}>
                   Image Search
                   <span style={{ fontSize:'9px', background:'#BBDEFB', color:'#0D47A1', padding:'1px 6px', borderRadius:'8px', fontWeight:'800' }}>AI</span>
                 </label>
                 <div style={{ display:'flex', gap:'6px' }}>
                   <input
                     type="file" accept="image/*" id="imgSearchInput" style={{ display:'none' }}
                     onChange={async (e) => {
                       const file = e.target.files[0];
                       if (!file) return;
                       setImgSearchLoading(true);
                       setImgSearchHint('');
                       try {
                         const reader = new FileReader();
                         reader.onload = async (ev) => {
                           const base64 = ev.target.result.split(',')[1];
                           const res = await fetch('https://api.anthropic.com/v1/messages', {
                             method: 'POST',
                             headers: { 'Content-Type': 'application/json' },
                             body: JSON.stringify({
                               model: 'claude-sonnet-4-20250514',
                               max_tokens: 200,
                               messages: [{ role: 'user', content: [
                                 { type: 'image', source: { type: 'base64', media_type: file.type, data: base64 } },
                                 { type: 'text', text: 'This is a garment style image from a fashion ERP. Extract any visible style reference codes, style numbers, or short identifiers you can see. Reply ONLY with a comma-separated list of keywords or codes to search, nothing else.' }
                               ]}]
                             })
                           });
                           const data = await res.json();
                           const hint = (data.content?.[0]?.text || '').trim();
                           setImgSearchHint(hint);
                           if (hint) setFilter(f => ({ ...f, style: hint.split(',')[0].trim() }));
                           setImgSearchLoading(false);
                           e.target.value = '';
                         };
                         reader.readAsDataURL(file);
                       } catch { setImgSearchLoading(false); }
                     }}
                   />
                   <label htmlFor="imgSearchInput"
                     style={{ flex:1, padding:'8px 10px', border:'1px dashed #6C757D', borderRadius:'4px',
                       cursor:'pointer', fontSize:'12px', color: imgSearchLoading ? '#7B1FA2' : '#6C757D',
                       background: imgSearchHint ? '#E3F2FD' : 'white',
                       textAlign:'center', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis',
                       display:'flex', alignItems:'center', justifyContent:'center', gap:'5px' }}>
                     {imgSearchLoading
                       ? '⏳ Analysing…'
                       : imgSearchHint
                         ? `🔍 ${imgSearchHint.split(',')[0].trim()}`
                         : '📷 Upload Style Image'}
                   </label>
                   {imgSearchHint && (
                     <button onClick={() => { setImgSearchHint(''); setFilter(f => ({...f, style:''})); }}
                       style={{ padding:'6px 8px', border:'none', background:'#F4F6F8', borderRadius:'4px', cursor:'pointer', fontSize:'12px', color:'#6C757D' }}>
                       ✕
                     </button>
                   )}
                 </div>
                 {imgSearchHint && (
                   <div style={{ fontSize:'10px', color:'#7B1FA2', marginTop:'3px' }}>
                     AI detected: <strong>{imgSearchHint}</strong>
                   </div>
                 )}
               </div>

             </div>
          </div>

          <div className="card" style={{padding:'0', overflow:'hidden'}}>
             <div className="scroll-x-container">
                {/* IMPROVED TABLE LAYOUT: SPACIOUS & CLEAN */}
                <table style={{width:'100%', borderCollapse:'collapse', fontSize:'0.9rem', color:'#212529'}}>
                   <thead>
                       <tr style={{background:'#F4F6F8', borderBottom:'2px solid #DEE2E6'}}>
                          <th style={{padding:'15px', textAlign:'left', fontWeight:'bold', color:'#6C757D'}}>IMAGE</th>
                          <th style={{padding:'15px', textAlign:'left', fontWeight:'bold', color:'#6C757D'}}>STYLE</th>
                          <th style={{padding:'15px', textAlign:'right', fontWeight:'bold', color:'#6C757D'}}>MAT</th>
                          <th style={{padding:'15px', textAlign:'right', fontWeight:'bold', color:'#6C757D'}}>TRIMS</th>
                          <th style={{padding:'15px', textAlign:'right', fontWeight:'bold', color:'#6C757D'}}>VA</th>
                          <th style={{padding:'15px', textAlign:'right', fontWeight:'bold', color:'#6C757D'}}>FINISH</th>
                          <th style={{padding:'15px', textAlign:'right', fontWeight:'bold', color:'#6C757D'}}>CM</th>
                          <th style={{padding:'15px', textAlign:'right', fontWeight:'bold', color:'#6C757D'}}>FOB ({baseCurrency.name || 'base'}) ★</th>
                          <th style={{padding:'15px', textAlign:'center', fontWeight:'bold', color:'#6C757D'}}>STATUS</th>
                          <th style={{padding:'15px', textAlign:'center', fontWeight:'bold', color:'#6C757D'}}>ACTIONS</th>
                       </tr>
                    </thead>
                    <tbody>
                       {dashboardData.map(s => {
                          const displayImg = s.workstationData?.snapshot || s.img;
                          const isLocked = s.costStatus.includes('Approved') || s.costStatus.includes('Rejected');

                          return (
                          <tr key={s.id} style={{background: s.hasCost ? 'white' : '#fcfcfc', borderBottom:'1px solid #F4F6F8', transition:'background 0.2s'}}>
                             <td style={{width:'80px', padding:'15px'}}>
                                 {displayImg ? (
                                   <img 
                                      src={displayImg} 
                                      key={displayImg} 
                                      style={{width:'50px', height:'50px', objectFit:'cover', borderRadius:'6px', border:'1px solid #CED4DA', cursor:'pointer'}} 
                                      onClick={()=>openStylePassport(s)}
                                      alt="Style"
                                   />
                                 ) : (
                                   <div style={{width:'50px', height:'50px', background:'#DEE2E6', borderRadius:'6px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.6rem', color:'#6C757D'}}>No Img</div>
                                )}
                             </td>
                             <td style={{padding:'15px', fontWeight:'600', color:'#1565C0', cursor:'pointer'}} onClick={()=>openStylePassport(s)}>
                                {s.styleRef} <div style={{fontSize:'0.8rem', color:'#6C757D', marginTop:'4px'}}>{s.brand}</div>
                             </td>
                             <td onClick={()=>{loadCosting(s.id); setActiveBlock("MATERIAL")}} style={{padding:'15px', textAlign:'right', cursor:'pointer'}}>{formatMoney(s.m)}</td>
                             <td onClick={()=>{loadCosting(s.id); setActiveBlock("TRIMS")}} style={{padding:'15px', textAlign:'right', cursor:'pointer'}}>{formatMoney(s.t)}</td>
                             <td onClick={()=>{loadCosting(s.id); setActiveBlock("VA")}} style={{padding:'15px', textAlign:'right', cursor:'pointer'}}>{formatMoney(s.v)}</td>
                             <td onClick={()=>{loadCosting(s.id); setActiveBlock("FINISHING")}} style={{padding:'15px', textAlign:'right', cursor:'pointer'}}>{formatMoney(s.f)}</td>
                             <td onClick={()=>{loadCosting(s.id); setActiveBlock("CM")}} style={{padding:'15px', textAlign:'right', fontWeight:'bold', color:'#7B1FA2', cursor:'pointer'}}>
                                {formatMoney(s.cm)}
                                <div style={{fontSize:'0.7rem', color:'#6C757D', fontWeight:'normal'}}>SMV: {s.sam.toFixed(3) || 0}</div>
                             </td>
                             <td style={{padding:'15px', textAlign:'right', fontWeight:'900', color:'#2E7D32', fontSize:'1.1rem', cursor:'pointer'}} onClick={()=>{loadCosting(s.id); setActiveBlock("SUMMARY")}}>{formatBaseMoney(s.fob)}</td>
                             <td style={{padding:'15px', textAlign:'center'}}>
                                <span style={{
                                   background: s.costStatus.includes('Approved') ? '#E8F5E9' : (s.costStatus.includes('Rejected') ? '#FFCDD2' : '#fff7ed'),
                                   color: s.costStatus.includes('Approved') ? '#166534' : (s.costStatus.includes('Rejected') ? '#991b1b' : '#9a3412'),
                                   padding: '6px 12px', borderRadius: '12px', fontWeight: 'bold', fontSize:'0.75rem', display:'inline-block'
                                }}>{s.costStatus}</span>
                             </td>
                             <td style={{padding:'15px', textAlign:'center'}}>
                                <div style={{display:'flex', gap:'8px', justifyContent:'center'}}>
                                    <button style={{background:'white', border:'1px solid #CED4DA', padding:'6px 12px', borderRadius:'4px', color:'#212529', fontWeight:'bold', cursor:'pointer'}} onClick={()=>{loadCosting(s.id); setActiveBlock("SUMMARY")}}>Edit</button>
                                    {!isLocked && s.hasCost && (
                                    <button 
                                        style={{background:'#C62828', color:'white', border:'none', padding:'6px 12px', borderRadius:'4px', cursor:'pointer', fontWeight:'bold', fontSize:'0.75rem'}}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if(window.confirm('Reset Costing? This will remove all financial data for this style.')) {
                                                deleteCosting(s.id);
                                            }
                                        }}
                                    >
                                        RESET
                                    </button>
                                    )}
                                </div>
                             </td>
                          </tr>
                       )})}
                       {dashboardData.length === 0 && <tr><td colSpan="10" style={{textAlign:'center', padding:'40px', color:'#6C757D', fontSize:'1.1rem'}}>No Styles Found matching filters.</td></tr>}
                    </tbody>
                  </table>
             </div>
          </div>
       </div>
    );
  }

  const cmBase = ws.cm.cost > 0 ? parseFloat(ws.cm.cost) : (ws.cm.sam * ws.cm.cpm)/(ws.cm.eff/100);
  const cmConverted = cmBase * conversionFactor;
  const fobConverted = fobTotal * conversionFactor;

  const getDispCPM = () => ws.cm.cpm ? (parseFloat(ws.cm.cpm) * conversionFactor).toFixed(3) : '';
  const handleCPM = (val) => {
      const floatVal = parseFloat(val);
      const baseVal = isNaN(floatVal) ? 0 : (floatVal / conversionFactor);
      setWs({...ws, cm: {...ws.cm, cpm: baseVal}});
  };

  const getDispManual = () => ws.cm.cost ? (parseFloat(ws.cm.cost) * conversionFactor).toFixed(3) : '';
  const handleManual = (val) => {
      const floatVal = parseFloat(val);
      const baseVal = isNaN(floatVal) ? 0 : (floatVal / conversionFactor);
      setWs({...ws, cm: {...ws.cm, cost: baseVal}});
  };

  return (
    <div className="layout-master" style={{ display: 'block' }}>
      <div style={{display:'flex', justifyContent:'space-between', marginBottom:'20px'}}>
         <div style={{display:'flex', gap:'5px', overflowX:'auto'}}>
            {["DASHBOARD","MATERIAL","TRIMS","VA","FINISHING","CM","SUMMARY"].map(n => (
              <button key={n} 
                style={{
                   padding:'8px 12px', borderRadius:'6px', border:'none', 
                   background: activeBlock===n?'#1565C0':'#CED4DA', 
                   color: activeBlock===n?'white':'#212529', 
                   fontWeight:'bold', cursor:'pointer', fontSize:'0.85rem'
                }}
                onClick={() => setActiveBlock(n)}
               >
                {n}
              </button>
            ))}
         </div>
         <div style={{display:'flex', gap:'10px'}}>
             <select 
                value={ws.status} 
                onChange={e=>setWs({...ws, status:e.target.value})} 
                style={{padding:'8px', borderRadius:'4px', border:'1px solid #FF8F00', background:'#FFF8E1', fontWeight:'bold', color:'#92400e'}}
             >
                <option>Pending IA</option><option>Pending BA</option><option>IApproved</option><option>BApproved</option><option>Rejected</option><option>Recost</option>
             </select>
             {/* CORRECTED CODE BLOCK */}
<button 
    onClick={() => saveCosting()} 
    className="btn-p" 
    style={{background:'#2E7D32'}}
>
    SAVE COSTING
</button>

<button 
    onClick={() => downloadExcel()} 
    className="btn-p" 
    style={{background:'#FF8F00'}}
>
    DOWNLOAD EXCEL
</button>
         </div>
      </div>
      
      <div className="fob-header">
         <div>
            <span style={{opacity:0.7, fontSize:'0.9rem', letterSpacing:'1px'}}>PROJECTED FOB COST</span><br/>
            <span className="fob-val">{formatMoney(fobConverted)}</span>
         </div>
         <div style={{textAlign:'right'}}>
            <span style={{opacity:0.7, fontSize:'0.9rem', color:'white'}}>SELECTED CURRENCY: </span>
            <select 
               style={{marginLeft:'10px', background:'rgba(255,255,255,0.1)', color:'white', border:'1px solid rgba(255,255,255,0.2)', padding:'5px', borderRadius:'4px', cursor:'pointer'}} 
               onChange={e => setCurrency(db.currencies.find(c => c.name === e.target.value) || currency)} 
               value={currency.name}
            >
                {(db.currencies || []).map(c => <option key={c.id} style={{color:'black'}} value={c.name}>{c.name} ({c.rate})</option>)}
             </select>
         </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'20px' }}>
        <div className="card">
           <h4>5. CM & EFFICIENCY</h4>
          <div className="form-row">
            <div className="field"><label>SMV</label><input type="number" value={ws.cm.sam} onChange={e => setWs({...ws, cm: {...ws.cm, sam: e.target.value}})} /></div>
            <div className="field"><label>Eff %</label><input type="number" value={ws.cm.eff} onChange={e => setWs({...ws, cm: {...ws.cm, eff: e.target.value}})} /></div>
            <div className="field">
               <label>CPM ({currency.name})</label>
               <input type="number" value={getDispCPM()} onChange={e => handleCPM(e.target.value)} />
            </div>
            <div className="field">
               <label>Manual Cost ({currency.name})</label>
               <input type="number" value={getDispManual()} onChange={e => handleManual(e.target.value)} />
            </div>
          </div>
          <div style={{marginTop:'10px', fontSize:'0.85rem', color:'#166534', background:'#f0fdf4', padding:'10px', borderRadius:'4px'}}>
             Current CM ({currency.name}): <strong>{formatMoney(cmConverted)}</strong>
          </div>
        </div>
        <div className="card">
          <h4>6. COMMERCIAL LOADING</h4>
          <div className="form-row">
             <div className="field"><label>Overheads %</label><input type="number" value={ws.comm.oh} onChange={e => setWs({...ws, comm: {...ws.comm, oh: e.target.value}})} /></div>
             <div className="field"><label>Profit %</label><input type="number" value={ws.comm.profit} onChange={e => setWs({...ws, comm: {...ws.comm, profit: e.target.value}})} /></div>
          </div>
          <div style={{ marginTop:'10px', background:'#E3F2FD', border:'1px solid #a8d1ff', borderRadius:'6px', padding:'10px 14px' }}>
            <div style={{ fontSize:'11px', color:'#6C757D', fontWeight:'700', marginBottom:'2px' }}>Total Commercial Loading</div>
            <div style={{ fontSize:'1.4rem', fontWeight:'900', color:'#0D47A1' }}>
              {(parseFloat(ws.comm.oh||0) + parseFloat(ws.comm.profit||0)).toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      <div className="card">
         <h4>COSTING BREAKDOWN (CLICK ROW TO EDIT)</h4>
        <table className="npd-table">
          <thead><tr><th>COST BLOCK</th><th>VALUE ({currency.name})</th><th>CONTRIBUTION</th></tr></thead>
          <tbody>
            <tr className="hub-jump" onClick={() => setActiveBlock("MATERIAL")}>
               <td>1. MATERIAL BLOCK</td>
               <td>{formatMoney(sumTotal('material'))}</td>
               <td>{fobConverted > 0 ? ((sumTotal('material')/fobConverted)*100).toFixed(1) : 0}%</td>
            </tr>
            <tr className="hub-jump" onClick={() => setActiveBlock("TRIMS")}>
               <td>2. TRIMS BLOCK</td>
               <td>{formatMoney(sumTotal('trims'))}</td>
               <td>{fobConverted > 0 ? ((sumTotal('trims')/fobConverted)*100).toFixed(1) : 0}%</td>
            </tr>
            <tr className="hub-jump" onClick={() => setActiveBlock("VA")}>
               <td>3. VA BLOCK</td>
               <td>{formatMoney(sumTotal('va'))}</td>
               <td>{fobConverted > 0 ? ((sumTotal('va')/fobConverted)*100).toFixed(1) : 0}%</td>
            </tr>
            <tr className="hub-jump" onClick={() => setActiveBlock("FINISHING")}>
               <td>4. FINISHING BLOCK</td>
               <td>{formatMoney(sumTotal('finishing'))}</td>
               <td>{fobConverted > 0 ? ((sumTotal('finishing')/fobConverted)*100).toFixed(1) : 0}%</td>
            </tr>
            <tr className="hub-jump" onClick={() => setActiveBlock("CM")}>
               <td>5. CM COST</td>
               <td>{formatMoney(cmConverted)}</td>
               <td>{fobConverted > 0 ? ((cmConverted/fobConverted)*100).toFixed(1) : 0}%</td>
            </tr>
            <tr style={{background:'#f0fdf4', fontWeight:'bold'}}>
               <td>SUB TOTAL (NET COST)</td>
               <td colSpan="2">{formatMoney(sumTotal('material')+sumTotal('trims')+sumTotal('va')+sumTotal('finishing')+cmConverted)}</td>
            </tr>
<tr className="fob-header" style={{margin: 0, padding: '10px 15px', borderRadius:'0 0 8px 8px'}}>
   <td style={{color:'white', border:0}}>GRAND TOTAL FOB</td>
   <td colSpan="2" style={{color:'#4ade80', fontSize:'1.2rem', fontWeight:'900'}}>
      {formatMoney(fobTotal)}
   </td>
</tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}